import { feedBaseUrl, baseUrl } from "./settings";
import { postApi } from "@/utils/http";
import odata from "@/utils/packageOdata";
import store from "@/store/index";

export function formSource(source) {
  if (source && source.external == false) {
    let urlToUse = "";
    if (!source.version || source.version === 1) urlToUse = feedBaseUrl;
    else urlToUse = `${baseUrl}/feeds`;

    source.url = `${urlToUse}/${source.uid}/user/${source.key}/nuget/`;
  }

  return source;
}

export function getLatestChocoPackageFilter(packageName, version, currentUrl) {
  let filter = "";
  if (version) {
    filter = `Id%20eq%20%27${packageName}%27%20and%20Version%20eq%20%27${version}%27`;
  } else {
    filter = `Id%20eq%20%27${packageName}%27%20and%20IsLatestVersion%20eq%20true`;
  }

  if (currentUrl && currentUrl !== "") return `${currentUrl}%20or%20${filter}`;
  else return `${odata.STRICT_FILTER_BASE_STRING}${filter}`;
}

export async function getChocoPackageFilter(sources, packages) {
  const filterArr = [];

  for (const source of sources) {
    let filter = "";

    const sourcePackages = packages.filter((p) => p.source.uid === source.uid);

    for (let i = 0; i <= sourcePackages.length; i++) {
      const p = sourcePackages[i];

      if (p) {
        const { name, version } = getPackageNameVersionAndHash(p);

        filterArr.push({
          filter: getLatestChocoPackageFilter(name, version),
          source: source,
        });
      }
    }
  }
  return filterArr;
}

export async function getChocoPackagesFromFilter(filterArr, strip = true) {
  const promiseArr = [];

  for (const currentFilter of filterArr) {
    promiseArr.push(
      postApi("choco/packages", {
        query: currentFilter.filter,
        source: currentFilter.source,
      })
    );
  }

  const promiseRes = await Promise.all(promiseArr);

  return [].concat.apply(
    [],
    promiseRes.map((x) => {
      if (strip) return stripPackages(x.packages);
      else return x.packages;
    })
  );

  // return removeChocoDuplicates(
  //   [].concat.apply(
  //     [],
  //     promiseRes.map((x) => {
  //       if (strip) return stripPackages(x.packages);
  //       else return x.packages;
  //     })
  //   )
  // );
}

export function getPackageNameVersionAndHash(p) {
  if (p.chocoPackage && p.chocoPackage.Id && p.chocoPackage.Version) {
    return {
      name: p.chocoPackage.Id,
      version: p.chocoPackage.Version,
      hash: p.chocoPackage.PackageHash,
      title: p.chocoPackage.Title,
      uid: p.source.uid,
    };
  } else if (p.package && p.package.name && p.version.version) {
    return {
      name: p.package.name,
      version: p.version.version,
      hash: p.version.packageHash,
      title: p.package.name,
      uid: p.source.uid,
    };
  }

  return null;
}

export function hasSamePackageName(p1, p2) {
  const p1NV = getPackageNameVersionAndHash(p1);
  const p2NV = getPackageNameVersionAndHash(p2);

  return p1NV.name === p2NV.name && p1.source.uid === p2.source.uid;
}

export function isSamePackage(p1, p2) {
  const p1NV = getPackageNameVersionAndHash(p1);
  const p2NV = getPackageNameVersionAndHash(p2);

  return (
    p1NV.name === p2NV.name &&
    p1NV.version === p2NV.version &&
    p1.source.uid === p2.source.uid
  );
}

export async function mergePackages(
  packages1,
  packages2,
  selected = false,
  addIfEmpty
) {
  const mergedArr = [];

  for (const p1 of packages1) {
    p1.selected = selected;

    const p2 = packages2.find((x) => isSamePackage(x, p1));

    if (p2) {
      mergedArr.push(mergePackage(p1, p2));
    } else {
      if (addIfEmpty) mergedArr.push(p1);
      else
        mergedArr.push(
          mergePackage(p1, createChocoPseudoPackageFromMDCPackage(p1))
        ); // console.log("not merged", p1.package.name, p1.source.uid, p1);
    }
  }

  return mergedArr;
}

export function mergePackage(p1, p2) {
  return Object.assign({ ...p1 }, { ...p2 });
}

export function hasParam(params, param) {
  if (params && param) {
    if (param && param.includes("|")) {
      param = param.replace("|", "");
    }

    const arr = params.split("|");
    return arr.some((x) => x.trim() == param);
  }

  return false;
}

export function toIsoDate(date) {
  if (date && date != "") {
    date = convertToDate(date);
    return date.toLocaleDateString([], {
      //weekday: "narrow",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    });
  }

  return "";
}

function convertToDate(dateStr) {
  if (dateStr) {
    var dateObj = new Date(dateStr);
    return dateObj;
  }

  return null;
}

export async function getChocoPackagesFromMdcPackages(mdcPackages, domainUID) {
  await store.dispatch("source/fetch", { domainUID });
  const filters = await getChocoPackageFilter(
    store.state.source.sources,
    mdcPackages
  );

  const chocoPackages = await getChocoPackagesFromFilter(filters);
  return await mergePackages(mdcPackages, chocoPackages);
}

export async function getChocoPackageFromMdcPackage(mdcPackage, domainUID) {
  await store.dispatch("source/fetch", { domainUID });

  const filters = await getChocoPackageFilter(store.state.source.sources, [
    mdcPackage,
  ]);

  const chocoRes = await getChocoPackagesFromFilter(filters);
  const chocoPackages = stripPackages(chocoRes);

  return chocoPackages[0];
}

export async function correctMissingChocoPackages(mdcPackages, chocoPackages) {
  chocoPackages = [...chocoPackages];
  const missingPackages = [];
  const packagesToKeep = [];

  for (const chocoPackage of chocoPackages) {
    const missing = mdcPackages.find(
      (x) =>
        !isSamePackage(x, chocoPackage, true) &&
        hasSamePackageName(x, chocoPackage, true)
    );

    if (missing) {
      missingPackages.push(missing);
    } else packagesToKeep.push(chocoPackage);
  }

  const filters = await getChocoPackageFilter(
    store.state.source.sources,
    missingPackages
  );

  const chocoRes = await getChocoPackagesFromFilter(filters);
  const newChocoPackages = stripPackages(chocoRes);

  return [...packagesToKeep, ...newChocoPackages];
}

export function stripPackages(packages) {
  return packages.map((x) => ({
    chocoPackage: x.chocoPackage,
    source: x.source,
  }));
}

export function removeChocoDuplicates(packages) {
  return packages.filter(
    (x, index) =>
      packages.findIndex(
        (y) =>
          x.chocoPackage.Id === y.chocoPackage.Id &&
          x.chocoPackage.PackageHash === y.chocoPackage.PackageHash &&
          x.source.uid === y.source.uid &&
          x.chocoPackage.Version === x.chocoPackage.Version
      ) === index
  );
}

export function removeMdcDuplicates(packages) {
  return packages; //.filter((x, index) => packages.indexOf(x) === index);
}

export function getGroup(item) {
  if (item.groupInfo) {
    if (item.groupInfo.autoGroup && item.groupInfo.isAutoUpgrade)
      return item.groupInfo.autoGroup;
    else if (item.groupInfo.group) return item.groupInfo.group;

    return null;
  }
}

export function createChocoPseudoPackageFromMDCPackage(item) {
  return {
    chocoPackage: {
      Id: item.package.name,
      Title: item.package.name,
      Version: item.version.version,
      PackageHash: item.version.packageHash,
      Pseudo: true,
    },
  };
}
